import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { cn } from '@/shadcn-ui/utils';
import { Button } from '@/shadcn-ui/button';
import { colors, colorsAlpha } from '@/theme/colors';
import CarouselArrowLeft from '@/icons/arrow-left';
import CarouselArrowRight from '@/icons/arrow-right';
import {
  ComponentProps,
  forwardRef,
  HTMLAttributes,
  useCallback,
  useEffect,
  KeyboardEvent,
  useState,
  createContext,
  useContext,
} from 'react';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  alwaysEnableDrag?: boolean;
  setApi?: (api: CarouselApi) => void;
};

type EmblaCarouselApi = ReturnType<typeof useEmblaCarousel>[1];

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: EmblaCarouselApi;
  scrollPrev: () => void;
  scrollNext: () => void;
  onDotClick: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  slidesNotInView: number[];
  selectedIndex: number;
  scrollSnaps: number[];
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

interface CarouselComponentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    CarouselProps {
  className?: string;
}

const Carousel = forwardRef<HTMLDivElement, CarouselComponentProps>(
  (
    {
      orientation = 'horizontal',
      opts = { duration: 23 },
      alwaysEnableDrag = true,
      setApi,
      plugins,
      className,
      children,
      ...props
    }: CarouselComponentProps,
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
        // Enable drag if some slides are not in view
        watchDrag: (api) => alwaysEnableDrag || api.slidesNotInView().length > 0,
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [slidesNotInView, setSlidesNotInView] = useState<number[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const onSelect = useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      setSelectedIndex(api.selectedScrollSnap());
      setScrollSnaps(api.scrollSnapList());
    }, []);

    const onSlidesInView = useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setSlidesNotInView(api.slidesNotInView());
    }, []);

    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const onDotClick = useCallback(
      (index: number) => {
        api?.scrollTo(index);
      },
      [api]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);
      api.on('slidesInView', onSlidesInView);

      return () => {
        api?.off('select', onSelect);
        api?.off('slidesInView', onSlidesInView);
      };
    }, [api, onSelect, onSlidesInView]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          onDotClick,
          canScrollPrev,
          canScrollNext,
          slidesNotInView,
          selectedIndex,
          scrollSnaps,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = 'Carousel';

interface CarouselContentProps extends HTMLAttributes<HTMLDivElement> {
  parentClassName?: string;
  childClassName?: string;
}
const CarouselContent = forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ parentClassName, childClassName, ...props }: CarouselContentProps, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div ref={carouselRef} className={cn('overflow-hidden', parentClassName)}>
        <div
          ref={ref}
          className={cn(
            'flex',
            orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
            childClassName
          )}
          {...props}
        />
      </div>
    );
  }
);
CarouselContent.displayName = 'CarouselContent';

interface CarouselItemProps extends HTMLAttributes<HTMLDivElement> {
  index?: number;
  scrollOnInteraction?: boolean;
}
const CarouselItem = forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, index, scrollOnInteraction, ...props }: CarouselItemProps, ref) => {
    const { orientation, api } = useCarousel();

    const handleInteraction = useCallback(() => {
      if (scrollOnInteraction && index !== undefined && api) {
        api.scrollTo(index);
      }
    }, [scrollOnInteraction, index, api]);

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={index}
        aria-roledescription="slide"
        className={cn(
          'min-w-0 shrink-0 grow-0 basis-full',
          orientation === 'horizontal' ? 'pl-4' : 'pt-4',
          className
        )}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleInteraction();
          }
        }}
        onClick={handleInteraction}
        onTouchStart={handleInteraction}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = 'CarouselItem';

interface CarouselPreviousProps extends ComponentProps<typeof Button> {
  size?: 'none';
}
const CarouselPrevious = forwardRef<HTMLButtonElement, CarouselPreviousProps>(
  (
    { className, variant = 'outline', size = 'none', ...props }: CarouselPreviousProps,
    ref
  ) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute h-8 w-8 rounded-full',
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="sr-only">Previous slide</span>
      </Button>
    );
  }
);
CarouselPrevious.displayName = 'CarouselPrevious';

interface CarouselNextProps extends ComponentProps<typeof Button> {
  size?: 'none';
}
const CarouselNext = forwardRef<HTMLButtonElement, CarouselNextProps>(
  (
    { className, variant = 'outline', size = 'none', ...props }: CarouselNextProps,
    ref
  ) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute h-8 w-8 rounded-full',
          orientation === 'horizontal'
            ? '-right-12 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          className
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <ArrowRight className="h-4 w-4" />
        <span className="sr-only">Next slide</span>
      </Button>
    );
  }
);
CarouselNext.displayName = 'CarouselNext';

const CarouselControls = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }: HTMLAttributes<HTMLDivElement>, ref) => {
    const {
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      selectedIndex,
      scrollSnaps,
      onDotClick,
    } = useCarousel();

    return (
      <div
        ref={ref}
        className={cn(
          'absolute bottom-[16px] left-0 right-0 z-1 mx-auto flex items-center justify-center gap-1 lg:bottom-[10px]',
          className
        )}
        {...props}
      >
        <Button
          size="none"
          className={cn(
            'hidden h-[24px] w-[24px] items-center justify-center rounded-full transition-colors md:flex',
            {
              'cursor-default': !canScrollPrev,
            }
          )}
          variant="primary"
          disabled={!canScrollPrev}
          onClick={scrollPrev}
        >
          <CarouselArrowLeft
            fill={canScrollPrev ? colors.neutral50 : colorsAlpha.neutral50Alpha25}
          />
        </Button>

        <div className="flex flex-wrap justify-center gap-[12px]">
          {scrollSnaps.map((_, index) => (
            <Button
              size="none"
              variant="primary"
              onClick={() => onDotClick(index)}
              key={`dot-${index}`}
            >
              <div
                className={cn(
                  'h-[10px] w-[10px] rounded-full',
                  selectedIndex === index
                    ? 'bg-flexcar-neutral20'
                    : 'bg-flexcar-neutral50'
                )}
              />
            </Button>
          ))}
        </div>

        <Button
          size="none"
          className={cn(
            'hidden h-[24px] w-[24px] items-center justify-center rounded-full transition-colors md:flex',
            {
              'cursor-default': !canScrollNext,
            }
          )}
          variant="primary"
          disabled={!canScrollNext}
          onClick={scrollNext}
        >
          <CarouselArrowRight
            fill={canScrollNext ? colors.neutral50 : colorsAlpha.neutral50Alpha25}
          />
        </Button>
      </div>
    );
  }
);
CarouselControls.displayName = 'CarouselControls';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselControls,
  useCarousel,
};
